import API_BASE from './api.js';

function getAuthToken() {
  return localStorage.getItem('idToken');
}

async function parseResponse(res) {
  const rawBody = await res.text();
  let data = {};

  try {
    data = rawBody ? JSON.parse(rawBody) : {};
  } catch {
    data = { error: rawBody || null };
  }

  return { rawBody, data };
}

function getQuickBooksErrorMessage(data, rawBody, fallback) {
  return (
    data?.error ||
    data?.details?.error ||
    data?.details?.Fault?.Error?.[0]?.Detail ||
    rawBody ||
    fallback
  );
}

async function syncQuickBooksDocument(endpoint, payload, fallbackMessage) {
  const token = getAuthToken();
  if (!token) {
    throw new Error('You must be signed in to sync with QuickBooks.');
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const { rawBody, data } = await parseResponse(res);

  if (!res.ok) {
    // Dated before the account's QuickBooks sync start date, so deliberately
    // not sent: a warning for the caller to show, not a failed save.
    if (data?.code === 'QB_BEFORE_SYNC_START_DATE') {
      return { skipped: true, warning: data.error };
    }
    const err = new Error(getQuickBooksErrorMessage(data, rawBody, fallbackMessage));
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export function shouldAutoCreateInQuickBooks(settings) {
  return Boolean(settings?.quickbooks?.autoCreateOnSave);
}

export async function autoCreateQuickBooksProposal({ proposalId, projectId }) {
  return syncQuickBooksDocument(
    '/api/quickbooks/create-estimate',
    { proposalId, projectId },
    'Failed to create QuickBooks proposal.'
  );
}

export async function autoUpdateQuickBooksProposal({ proposalId, projectId }) {
  return syncQuickBooksDocument(
    '/api/quickbooks/update-estimate',
    { proposalId, projectId },
    'Failed to update QuickBooks proposal.'
  );
}

export async function autoCreateQuickBooksInvoice({ invoiceId, projectId }) {
  return syncQuickBooksDocument(
    '/api/quickbooks/create-invoice',
    { invoiceId, projectId },
    'Failed to create QuickBooks invoice.'
  );
}

export async function autoUpdateQuickBooksInvoice({ invoiceId, projectId }) {
  return syncQuickBooksDocument(
    '/api/quickbooks/update-invoice',
    { invoiceId, projectId },
    'Failed to update QuickBooks invoice.'
  );
}

export async function autoCreateQuickBooksPurchaseOrder({ poId, projectId }) {
  return syncQuickBooksDocument(
    '/api/quickbooks/create-purchase-order',
    { poId, projectId },
    'Failed to create QuickBooks bill.'
  );
}

export async function autoUpdateQuickBooksPurchaseOrder({ poId, projectId }) {
  return syncQuickBooksDocument(
    '/api/quickbooks/update-purchase-order',
    { poId, projectId },
    'Failed to update QuickBooks bill.'
  );
}
